import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from './button';

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-3xl',
  '3xl': 'max-w-5xl',
};

export function Dialog({
  open,
  onClose,
  size = 'md',
  children,
  className,
  closeOnBackdrop = true,
  hideClose = false,
}) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    if (open) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{
        background: 'rgba(4,2,10,0.65)',
        backdropFilter: 'blur(8px)',
        animation: 'dialog-fade 0.2s ease-out',
      }}
      onClick={() => closeOnBackdrop && onClose?.()}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative w-full rounded-2xl',
          SIZES[size],
          className
        )}
        style={{
          background: 'var(--dash-surface)',
          border: '1px solid var(--dash-border-strong)',
          boxShadow:
            '0 24px 64px rgba(0,0,0,0.4), 0 0 0 1px var(--dash-primary-border), inset 0 1px 0 rgba(255,255,255,0.04)',
          animation: 'dialog-scale 0.25s ease-out',
        }}
      >
        {!hideClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
            style={{
              background: 'var(--dash-surface-subtle)',
              border: '1px solid var(--dash-border)',
              color: 'var(--dash-text-soft)',
            }}
          >
            <X size={14} />
          </button>
        )}
        {children}
      </div>
      <style>{`
        @keyframes dialog-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes dialog-scale {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>,
    document.body
  );
}

export function DialogHeader({ className, title, description, icon: Icon, ...props }) {
  return (
    <div
      className={cn('flex items-start gap-4 px-6 pb-4 pt-6', className)}
      {...props}
    >
      {Icon && (
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{
            background: 'var(--dash-primary-soft)',
            border: '1px solid var(--dash-primary-border)',
            color: 'var(--dash-primary)',
          }}
        >
          <Icon size={18} strokeWidth={2.2} />
        </div>
      )}
      <div className="flex flex-col gap-1 pr-8">
        {title && (
          <h2
            className="text-base font-semibold tracking-tight"
            style={{ color: 'var(--dash-text)' }}
          >
            {title}
          </h2>
        )}
        {description && (
          <p
            className="text-xs leading-relaxed"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

export function DialogContent({ className, children, ...props }) {
  return (
    <div className={cn('px-6 pb-2', className)} {...props}>
      {children}
    </div>
  );
}

export function DialogFooter({ className, children, ...props }) {
  return (
    <div
      className={cn(
        'mt-2 flex items-center justify-end gap-2 border-t px-6 py-4',
        className
      )}
      style={{ borderColor: 'var(--dash-border)' }}
      {...props}
    >
      {children}
    </div>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Tem certeza?',
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  loading = false,
}) {
  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <DialogHeader title={title} description={description} icon={AlertTriangle} />
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          variant={variant === 'danger' ? 'danger-solid' : 'primary'}
          onClick={onConfirm}
          loading={loading}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
